import * as React from 'react';
import {useEffect, useState} from 'react';
import {IonContent, IonPage} from '@ionic/react';
import ReactMapGl, {Layer, Popup, Source, NavigationControl} from 'react-map-gl';

import Header from '../../components/Header/Header';
import ProbePopup from '../../components/Maps/ProbePopup';
import {authHeader, titanTransformRequest, waitForIdToken} from '../../services/idToken';
import {
    API,
    CONSTRUCTION,
    FASTER,
    MIN_PTI,
    MIN_SEC_PER_MI,
    PTI_RAMP,
    SLOWER,
    anomalyPaint,
    reliabilityPaint,
} from './probeLayers';
import './ProbeMap.css';

const accessToken = import.meta.env.REACT_APP_MAPBOX_API_KEY;

type LayerKey = 'anomalies' | 'reliability';

// Probe speeds and work zones moved to the live stream map, where they sit
// under the cameras watching those roads. What is left here is the historical
// pair: how a segment is running against how it usually runs.
const LAYERS: {key: LayerKey; label: string; hint: string}[] = [
    {key: 'anomalies', label: 'Speed change', hint: 'Slower or faster than usual'},
    {key: 'reliability', label: 'Travel time reliability', hint: 'Planning time index'},
];

/**
 * The probe map from TITAN's MERCI app: HERE probe speeds as vector tiles,
 * with speed change and travel-time reliability drawn over them. Same
 * endpoints, same colour scales -- see probeLayers.ts.
 */
const ProbeMap: React.FC = () => {
    const [viewport, setViewport] = useState({
        latitude: 38.65, longitude: -90.4, zoom: 8.5,
    });
    const [active, setActive] = useState<Record<LayerKey, boolean>>(
        {anomalies: true, reliability: false});
    // These layers come from the TITAN API behind a Firebase token.
    const [tokenReady, setTokenReady] = useState(false);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const [anomalies, setAnomalies] = useState<any>(null);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const [reliability, setReliability] = useState<any>(null);
    const [error, setError] = useState('');
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const [popup, setPopup] = useState<{lng: number; lat: number; layer: string; p: any} | null>(null);

    useEffect(() => {
        let cancelled = false;
        waitForIdToken().then(ok => !cancelled && setTokenReady(ok));
        return () => { cancelled = true; };
    }, []);

    useEffect(() => {
        if (!tokenReady) {
            return;
        }
        let cancelled = false;
        // Name the layer that failed: "some layers" sent us hunting when the
        // work zone endpoint was 500ing and the map simply looked empty.
        const load = async (label: string, path: string, set: (v: unknown) => void) => {
            try {
                const res = await fetch(`${API}${path}`, {headers: await authHeader()});
                if (!res.ok) {
                    throw new Error(String(res.status));
                }
                const data = await res.json();
                if (!cancelled) set(data);
            } catch (e) {
                if (!cancelled) {
                    setError(`${label} could not be loaded (${(e as Error).message}).`);
                }
            }
        };
        load('Speed change', `/v1/segments/anomalies.geojson?min_sec_per_mi=${MIN_SEC_PER_MI}`, setAnomalies);
        load('Reliability', `/v1/segments/reliability.geojson?period=pm_peak&min_pti=${MIN_PTI}`, setReliability);
        return () => { cancelled = true; };
    }, [tokenReady]);

    const toggle = (key: LayerKey) =>
        setActive(current => ({...current, [key]: !current[key]}));

    // Only layers that are on can be clicked, so a hidden layer cannot answer
    // for a segment the user can see.
    const interactiveLayerIds = [
        active.anomalies && anomalies ? 'anomaly-line' : null,
        active.reliability && reliability ? 'reliability-line' : null,
    ].filter(Boolean) as string[];

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const onMapClick = (event: any) => {
        const feature = event.features?.[0];
        if (!feature) {
            setPopup(null);
            return;
        }
        const [lng, lat] = event.lngLat;
        setPopup({lng, lat, layer: feature.layer.id, p: feature.properties ?? {}});
    };

    return (
        <IonPage>
            <Header title="Probe" />
            <IonContent fullscreen className="page-content">
                <div className="probe-stage">
                    {accessToken ? (
                        <ReactMapGl
                            {...viewport}
                            onViewportChange={setViewport}
                            onClick={onMapClick}
                            interactiveLayerIds={interactiveLayerIds}
                            width="100%"
                            height="100%"
                            mapStyle="mapbox://styles/mapbox/dark-v10"
                            mapboxApiAccessToken={accessToken}
                            transformRequest={titanTransformRequest}
                        >
                            {active.reliability && reliability && (
                                <Source id="reliability" type="geojson" data={reliability}>
                                    <Layer id="reliability-line" type="line" paint={reliabilityPaint} />
                                </Source>
                            )}

                            {active.anomalies && anomalies && (
                                <Source id="anomalies" type="geojson" data={anomalies}>
                                    <Layer id="anomaly-line" type="line" paint={anomalyPaint} />
                                </Source>
                            )}

                            {popup && (
                                <Popup
                                    longitude={popup.lng}
                                    latitude={popup.lat}
                                    onClose={() => setPopup(null)}
                                    closeOnClick={false}
                                    captureScroll={true}
                                >
                                    <ProbePopup layer={popup.layer} p={popup.p} />
                                </Popup>
                            )}

                            <NavigationControl style={{right: 12, top: 12}} />
                        </ReactMapGl>
                    ) : (
                        <div className="map-unavailable">
                            <p className="map-unavailable__title">Map unavailable</p>
                            <p className="map-unavailable__detail">
                                REACT_APP_MAPBOX_API_KEY is not set, so the base map cannot load.
                            </p>
                        </div>
                    )}

                    <div className="probe-panel">
                        <h2 className="probe-panel__title">Layers</h2>
                        {LAYERS.map(layer => (
                            <label className="probe-toggle" key={layer.key}>
                                <input
                                    type="checkbox"
                                    checked={active[layer.key]}
                                    onChange={() => toggle(layer.key)}
                                />
                                <span>
                                    <span className="probe-toggle__label">{layer.label}</span>
                                    <span className="probe-toggle__hint">{layer.hint}</span>
                                </span>
                            </label>
                        ))}

                        <div className="probe-legend">
                            {active.anomalies && (
                                <>
                                    <div className="probe-legend__row">
                                        <span className="probe-legend__dot" style={{background: SLOWER}} />
                                        <span>slower than usual</span>
                                    </div>
                                    <div className="probe-legend__row">
                                        <span className="probe-legend__dot" style={{background: FASTER}} />
                                        <span>faster than usual</span>
                                    </div>
                                    <div className="probe-legend__row">
                                        <span className="probe-legend__dot" style={{background: CONSTRUCTION}} />
                                        <span>work zone explains it</span>
                                    </div>
                                </>
                            )}
                            {active.reliability && (
                                <div className="probe-legend__row">
                                    <span className="probe-legend__dot" style={{background: PTI_RAMP[3][1]}} />
                                    <span>least reliable (PTI {PTI_RAMP[3][0]}+)</span>
                                </div>
                            )}
                        </div>

                        {!tokenReady && <p className="probe-panel__note">Signing in to the data service...</p>}
                        {error && <p className="probe-panel__error">{error}</p>}
                    </div>
                </div>
            </IonContent>
        </IonPage>
    );
};

export default ProbeMap;
